import type { FigureSpec } from './figures'
import type { IconName } from './icons'
import { cachedTutorial } from './preferences'
import { withParams } from './tabs'

/**
 * Four walkthroughs, read alongside the console rather than acted out on top of it.
 *
 * The product's central workflow is four jobs that each span two or three screens: author a
 * dictionary, author a policy, connect them, and get an agent onto a machine. Nothing in the console
 * walked anybody through any of them, and a new operator's first act is authoring a document that
 * decides what runs on every workstation in a company.
 *
 * **Read-along rather than a guided tour, and the deciding reason is the fourth one.** Getting an
 * agent onto a machine means copying a folder to somebody's desk and running a script there,
 * unelevated. A tour that highlights real controls cannot cover the step that actually installs the
 * agent, so it would be a tour of the easy half - and it would break silently every time a screen is
 * rearranged, because it would depend on where controls are rather than on what they do. A step with
 * no `link` is that argument made visible: `tutorials.test.ts` asserts at least one exists.
 *
 * **Short, and drawn where a drawing is quicker.** The first version of this file was two long
 * sentences a step, most of them explaining why the console is shaped the way it is. That belongs in
 * `CONSOLE.md`; a card read beside a screen has to say what to do. So a step is a verb-first heading,
 * at most two short lines with the control's own label in bold, and a figure where one says it faster
 * - and `tutorials.test.ts` holds the lines to a length somebody can take in without rereading.
 *
 * What can be checked is checked: every `link.to` has to resolve to a help entry that exists, because
 * a walkthrough whose "Open the Dictionaries tab" button lands on a catch-all teaches the reader to
 * stop trusting the other five steps. That is the same argument `lib/shortcuts.ts` makes about
 * advertising a key that does nothing.
 */
export type TutorialId = 'dictionary' | 'policy' | 'binding' | 'deploy'

export interface TutorialStep {
  /** A verb first, and short enough to read at a glance: "Bind by name". */
  title: string
  /**
   * At most two lines, each short enough to take in without rereading, and asserted.
   *
   * A control's own label is written `**like this**` and drawn in bold, so the reader can match the
   * word on the card to the word on the screen. That is the only markup, and `emphasis` below is the
   * whole of its parser.
   */
  body: readonly string[]
  /**
   * Where this step happens, when it happens in this console.
   *
   * Absent when it does not. Copying a folder to a machine and running `install.ps1` happen on
   * somebody's desk, and a step with no link is the shape of the reason a guided tour was rejected
   * rather than an omission.
   */
  link?: { label: string; to: string }
  /** A drawing of what the step describes, where one says it faster than the words do. */
  figure?: FigureSpec
}

export interface Tutorial {
  id: TutorialId
  title: string
  /** Drawn beside the title in the Help menu and on the card, so the four can be told apart at a glance. */
  icon: IconName
  /** What the reader has at the end, written so somebody can decide not to start. */
  outcome: string
  steps: readonly TutorialStep[]
}

export const TUTORIALS: readonly Tutorial[] = [
  {
    id: 'dictionary',
    title: 'Create and edit a dictionary',
    icon: 'book-open',
    outcome: 'A named set of terms, published, ready for a policy to bind.',
    steps: [
      {
        title: 'Open the Dictionaries tab',
        body: [
          'Dictionaries live under **Policies**, because they are an input to a policy.',
          'Eight collections ship with every organization. They are ordinary dictionaries, and you can edit them.'
        ],
        link: { label: 'Open Dictionaries', to: '/policies?tab=dictionaries' },
        figure: { id: 'flow', focus: [1] }
      },
      {
        title: 'Name it, then create it',
        body: [
          'Type a name first. **Create** stays disabled until there is one.',
          'Pick a name people will search for: the list is sorted and filtered by it.'
        ]
      },
      {
        title: 'Add a rule',
        body: [
          '**Add a rule** asks for a name, terms one per line, and a severity.',
          'Severity is what an alert inherits when the rule matches.'
        ],
        figure: 'dictionary-rule'
      },
      {
        title: 'Edit a rule from its row',
        body: [
          '**Edit** reopens the same dialog with the terms filled in.',
          'Renaming a rule there saves a second rule rather than moving the one you opened.'
        ]
      },
      {
        title: 'Try it before anybody depends on it',
        body: [
          '**Show what would match** runs your terms against sample text and says which rule caught what.',
          'Single-word terms are allowed but match widely. The guidance panel flags them rather than refusing.'
        ],
        figure: 'dictionary-rule'
      },
      {
        title: 'Publish the revision',
        body: [
          '**Publish** freezes this revision. Your next edit starts a new one.',
          'Nothing reaches a device yet: a dictionary gets to the fleet only through a policy that binds it.'
        ],
        // `/policies/new` rather than back to the tab the reader is already on. The step says a
        // dictionary reaches the fleet only through a policy that binds it, and a button that returns
        // somebody to the dictionary list answers a sentence about policies with the wrong screen.
        link: { label: 'Start the policy that will carry it', to: '/policies/new' },
        figure: 'revision-publish'
      }
    ]
  },
  {
    id: 'policy',
    title: 'Add or edit a policy',
    icon: 'scroll',
    outcome: 'A policy saved, approved and published to every enrolled device.',
    steps: [
      {
        title: 'Start from the Policies tab',
        body: [
          '**New policy** opens the builder, which asks three questions instead of showing a form.',
          'Policies are removed from this list, in bulk. Policies built together are listed together.'
        ],
        link: { label: 'Open the builder', to: '/policies/build' },
        figure: 'policy-builder'
      },
      {
        title: 'Choose what to watch',
        body: [
          'An AI assistant such as Claude or ChatGPT, or the whole computer for an inventory or a screenshot.',
          'Watching an assistant needs prompt observation, which the page says release agents may lack.'
        ],
        figure: { id: 'policy-builder', focus: [1] }
      },
      {
        title: 'Tick every place it runs',
        body: [
          'In a browser, the desktop app, or a terminal. Each one you tick becomes its own policy.',
          'They stay together as one group, so they are approved and published as one decision.'
        ],
        figure: { id: 'policy-builder', focus: [2, 4] }
      },
      {
        title: 'Pick what should happen',
        body: [
          'Flag, send, block, or flag a personal account. A choice that cannot work here says why.',
          'Choose the dictionaries to match against, and whether to take a screenshot when one is flagged.'
        ],
        figure: { id: 'policy-builder', focus: [3] }
      },
      {
        title: 'Approve all, then publish all',
        body: [
          'The drafts reach no device until a person approves them. **Approve all** is all or nothing.',
          '**Publish all** checks every policy compiles first, then asks with what each one replaces.'
        ],
        figure: 'save-approve-publish'
      },
      {
        title: 'Change one setting under Advanced',
        body: [
          'Each policy in a group has **Advanced settings**: the full form, its schedule, and the JSON.',
          'An edit made there is marked on the group page, and publishing the group includes it.'
        ],
        figure: 'policy-anatomy'
      },
      {
        title: 'Or build one entirely by hand',
        body: [
          '**Start from scratch** opens the full editor, with templates, for anything the builder lacks.',
          'Every finding comes from the server, the same validator the save uses, before you can save.'
        ],
        link: { label: 'Open the full editor', to: '/policies/new' },
        figure: { id: 'policy-anatomy', focus: [1, 2] }
      }
    ]
  },
  {
    id: 'binding',
    title: 'Connect a dictionary to a policy',
    icon: 'link-simple',
    outcome: 'A policy carrying a dictionary by reference, published, with the terms on the fleet.',
    steps: [
      {
        title: 'Start a policy that watches prompts',
        body: [
          'The quickest start is the builder: its sensitive-prompt choices ask for dictionaries directly.',
          'Bound there, nothing below is needed. The steps that follow are for a policy that already exists.'
        ],
        link: { label: 'Open the builder', to: '/policies/build' },
        figure: { id: 'flow', focus: [1, 2] }
      },
      {
        title: 'Save the draft first',
        body: ['A binding needs a saved draft to belong to.', 'Press **Save**, and the search box appears.']
      },
      {
        title: 'Bind by name',
        body: [
          'Search, then press **Bind**. The binding saves at once.',
          'It does not publish. What a policy carries and when the fleet gets it are separate decisions.'
        ],
        figure: 'bind-by-reference'
      },
      {
        title: 'Check the merged count',
        body: [
          '**Once merged** shows how many rules the policy will carry, against what the agent accepts.',
          'Publishing is refused while a bound dictionary has no published revision.'
        ]
      },
      {
        title: 'Publish, and see nothing was copied',
        body: [
          'Open the JSON view. The terms are not there: only a reference, resolved when you publish.',
          'To correct a term, publish the dictionary, then republish the policies that bind it.'
        ],
        figure: 'bind-by-reference'
      }
    ]
  },
  {
    id: 'deploy',
    title: 'Build a kit and get an agent onto a machine',
    icon: 'package',
    outcome: 'A machine enrolled, checking in, and running the policies this organization has published.',
    steps: [
      {
        title: 'Read the strip above the list',
        body: [
          'It shows the agent build registered on this deployment, and the device seats left.',
          'If that build is not an installer, a warning stays up. Building is still allowed.'
        ],
        link: { label: 'Open Enrollment', to: '/devices?tab=deployment' }
      },
      {
        title: 'Build a kit',
        body: [
          'Give it a name you will find in six months, then build it.',
          'The zip holds the installer, **install.ps1**, your published policies and a live token.'
        ],
        figure: 'kit-contents'
      },
      {
        title: 'Know that it downloads once',
        body: [
          'The zip is never stored, and the server keeps only a hash of the token.',
          'To give a team one again, use **Build another like this**: new kit, new token, same name.'
        ]
      },
      {
        title: 'Copy the whole folder to the machine',
        body: [
          'The folder, not just the installer. install.ps1 checks the installer against the manifest.',
          'This happens on the machine rather than in this console, so there is nothing to click here.'
        ]
      },
      {
        title: 'Run install.ps1 as the person who uses the machine',
        body: [
          'Open PowerShell as that person. Not as an administrator, and never as SYSTEM.',
          'The agent installs per user. Installed elevated, it lands in a profile nobody signs in to.'
        ],
        figure: 'install-as-user'
      },
      {
        title: 'Watch it arrive',
        body: [
          'The machine appears on **Fleet** within a minute of its first check-in.',
          'Checking in is not the same as running policies. The columns there say which.'
        ],
        link: { label: 'Open the Fleet tab', to: '/devices' }
      },
      {
        title: 'Decide how long it may be away',
        body: [
          'The check-in interval and credential lifetime decide how long a machine can be off and still return.',
          'A credential rotates only at a check-in, so slowing the fleet down asks first.'
        ],
        link: { label: 'Open Check-in', to: '/devices?tab=checkin' },
        figure: 'checkin-window'
      },
      {
        title: 'Know how to take a way in back',
        body: [
          '**Revoke** withdraws a kit or token for good. Every kit built around that token stops working.',
          'Machines that already joined keep working and keep reporting.'
        ],
        link: { label: 'Back to Enrollment', to: '/devices?tab=deployment' }
      }
    ]
  }
]

/**
 * A step's line, split into plain runs and the control labels written `**like this**`.
 *
 * The whole grammar is that pair of asterisks, which is why this is a few lines rather than a
 * markdown library. An unpaired `**` is left as text rather than guessed at: a sentence that renders
 * with two stray asterisks is a visible typo, and a sentence half of which is bold is a quiet one.
 */
export function emphasis(line: string): { text: string; strong: boolean }[] {
  const parts = line.split('**')
  if (parts.length % 2 === 0) return [{ text: line, strong: false }]

  return parts
    .map((text, index) => ({ text, strong: index % 2 === 1 }))
    .filter((part) => part.text.length > 0)
}

/** The same line with its markup removed, for measuring and for searching. */
export function plain(line: string): string {
  return emphasis(line)
    .map((part) => part.text)
    .join('')
}

export function tutorialById(id: string): Tutorial | null {
  return TUTORIALS.find((tutorial) => tutorial.id === id) ?? null
}

/**
 * The tutorial an id names, for a caller holding a `TutorialId` rather than a string off a URL.
 *
 * Separate from `tutorialById` so that the one place allowed to meet an id that does not exist - a
 * pasted link, a value an older build stored - is the one that answers null, and every caller that
 * already holds a checked id does not have to assert its way past a null it cannot get.
 */
export function tutorialFor(id: TutorialId): Tutorial {
  const tutorial = tutorialById(id)
  if (!tutorial) throw new Error(`No walkthrough is declared for ${id}.`)
  return tutorial
}

/** The tutorial a URL has open, or null. */
export function tutorialFrom(search: string): Tutorial | null {
  return tutorialById(new URLSearchParams(search).get('help') ?? '')
}

/**
 * Which step a URL is on, one-based and clamped into the tutorial's range.
 *
 * Clamped rather than validated, because the number arrives from a pasted link and from storage
 * written by an older build. A `step=99` against a tutorial that has since lost a step opens the last
 * step, which is a readable answer; refusing would be a card that will not open and no way to see
 * why.
 */
export function stepFrom(search: string, tutorial: Tutorial): number {
  const raw = Number(new URLSearchParams(search).get('step'))
  if (!Number.isFinite(raw)) return 1
  return Math.min(Math.max(Math.trunc(raw), 1), tutorial.steps.length)
}

/** A search string with this tutorial open at this step, leaving everything else in place. */
export function withTutorial(search: string, id: TutorialId, step: number): string {
  return withParams(search, { help: id, step: String(step) })
}

/** The same string with no tutorial open. Used when a walkthrough is put down or finished. */
export function withoutTutorial(search: string): string {
  return withParams(search, { help: null, step: null })
}

/**
 * Which walkthrough and step to offer carrying on with, or null.
 *
 * The precedence rule, in one place because two homes for one fact can otherwise disagree quietly:
 * **a URL that carries `help` wins, and storage is consulted only when the URL says nothing.** Paste
 * a link at step 2 while storage holds step 5 and the answer is step 2, because the link is what was
 * just asked for.
 *
 * Storage exists at all because the query does not survive two of this console's own navigations - a
 * `NavLink` to a bare path drops it, and `CasesPage` discards it on a tab change - so a resume offer
 * built on the query alone would be absent at random from the operator's point of view.
 *
 * `fromUrl` says which of the two answered, because the card treats them differently: a place a link
 * carried is shown whether or not the reader put the walkthrough down earlier, since the link is a
 * fresh request, while a stored place that was put down is only *offered*.
 *
 * Every wrong value resolves to null or to a step that exists: a tutorial this build no longer has, a
 * step past the end of one that lost a step, a negative number. That is the same doctrine
 * `resolveLayout` applies to a saved dashboard, and for the same reason - the value was written by a
 * build that is not this one.
 */
export function resumeFor(
  search: string
): { tutorial: Tutorial; step: number; fromUrl: boolean; putDown: boolean } | null {
  const fromUrl = tutorialFrom(search)
  if (fromUrl) return { tutorial: fromUrl, step: stepFrom(search, fromUrl), fromUrl: true, putDown: false }

  const stored = cachedTutorial()
  if (!stored) return null

  const tutorial = tutorialById(stored.id)
  if (!tutorial) return null

  return {
    tutorial,
    step: Math.min(Math.max(stored.step, 1), tutorial.steps.length),
    fromUrl: false,
    putDown: stored.putDown
  }
}
